import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { PaginatedResponse } from '@/types';
import { API_URL } from './api-config';
import { setupInterceptors } from './api-interceptors';

/**
 * Extract the download filename from a `Content-Disposition` header, e.g.
 * `attachment; filename="ACME_2026-06_USD.csv"`. Returns null when the header is
 * absent (or not exposed by CORS), so callers can fall back to a client-side name.
 */
export function parseContentDispositionFilename(
  header?: string,
): string | null {
  if (!header) return null;
  // RFC 5987 `filename*=charset'lang'value` takes precedence over plain `filename=`.
  // Anchor the parameter name (start / `;` / whitespace) so `xfilename=` can't
  // false-match a different parameter.
  const ext = /(?:^|[;\s])filename\*=([^;]+)/i.exec(header);
  if (ext) {
    // Strip the `charset'lang'` prefix, then percent-decode. A malformed sequence
    // (bad `%`, non-UTF-8 charset) or an empty value must NOT abort the download -
    // fall through to the plain form / null instead.
    const value = ext[1].trim().replace(/^"|"$/g, '').replace(/^[\w-]+'[^']*'/, '');
    try {
      const decoded = decodeURIComponent(value);
      if (decoded) return decoded;
    } catch {
      /* malformed percent-encoding - fall through */
    }
  }
  const plain = /(?:^|[;\s])filename=("?)([^";]+)\1/i.exec(header);
  return plain ? plain[2].trim() : null;
}

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      timeout: 30000,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    setupInterceptors(this.client);
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  /**
   * Download an authorised file: returns the response blob plus the filename
   * parsed from `Content-Disposition`. A bare `<a href>` can't carry the auth
   * cookie/headers, so file downloads must go through this api layer.
   */
  async getFile(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<{ blob: Blob; filename: string | null }> {
    const response = await this.client.get<Blob>(url, {
      ...config,
      responseType: 'blob',
    });
    const filename = parseContentDispositionFilename(
      response.headers['content-disposition'],
    );
    return { blob: response.data, filename };
  }

  async getPaginated<T>(
    url: string, 
    params?: Record<string, unknown>
  ): Promise<PaginatedResponse<T>> {
    const response = await this.client.get<PaginatedResponse<T>>(url, { params });
    return response.data;
  }

}

export const apiService = new ApiService();
export { API_URL };

